import { SolidContainer, SolidLeaf, SolidLeafUri } from "@ldo/connected-solid";
import { useLdo, useSolidAuth } from "@ldo/solid-react";
import { ChangeEvent, createContext, createRef, FunctionComponent, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ONTOLOGY } from "../App";
import { LinkButton } from "../components/LinkButton";
import { SentenceLink } from "../components/SentenceLink";
import { WorkspaceLink } from "../components/WorkspaceLink";
import { useWsContext } from "../components/WsContext";
import { cmpResource, cmpStr } from "../lib/cmp";
import { lastPart } from "../lib/lastPart";
import { makeNamedNode } from "../lib/nodes";
import { rdf, rdfs, spoty } from "../lib/ns";
import { ImportInbox } from "../components/ImportInboxContent"
import { ShareCollectionModal } from "../components/ShareCollection"
// CSS
import "./WorkspacePage.css";

// This component displays all available sentences,
// showing the hierarchy of containers, sentence collections (TTL files) and sentences.
//
// TODO add features for
// - creating new folder
// - renaming/moving leafs?
// - manage non-TTL leafs?
// - removing empty container?
export const WorkspacePage: FunctionComponent = () => {
  const wsCtx = useWsContext();
  const workspace = wsCtx.workspace;
  console.debug("WorkspacePage rendering");
  const [converter, setConverter] = useState<Converter>();
  const [showImportModal, setShowImportModal] = useState(false);
  const [fileNumber, setFileNumber] = useState<number>();

  useEffect(() => {
    const worker = new Worker(new URL('../workers/convert.worker.ts', import.meta.url));

    const pendingConverts = new Map<string, { resolve: (ttl: string) => void, reject: (err: Error) => void }>();
    let convertId = 0;

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (msg.type === 'ready') {
        setConverter({
          convert: (data: Uint8Array, filename: string) => {
            return new Promise<string>((resolve, reject) => {
              const id = String(++convertId);
              pendingConverts.set(id, { resolve, reject });
              worker.postMessage({ type: 'convert', id, data, filename }, [data.buffer]);
            });
          },
        });
      } else if (msg.type === 'result') {
        const pending = pendingConverts.get(msg.id);
        if (pending) {
          pendingConverts.delete(msg.id);
          pending.resolve(msg.ttl);
        }
      } else if (msg.type === 'error') {
        const pending = pendingConverts.get(msg.id);
        if (pending) {
          pendingConverts.delete(msg.id);
          pending.reject(new Error(msg.error));
        }
      }
    };

    fetch(ONTOLOGY).then(resp => resp.text()).then(ontology => {
      worker.postMessage({ type: 'init', ontology });
    });

    return () => {
      worker.terminate();
    };
  }, []);

  const handleShowImportModal = () => {
    if (workspace.type !== "SolidLeaf") {
      setShowImportModal(true);
    }
  };

  return <div className="WorkspacePage">
    <ConverterCtx.Provider value={converter}>
      <ul className="sentences">
        {workspace.type === "SolidContainer"
          ? <><ContainerItem container={workspace} root />
            <ImportInbox workspace={workspace} setFileNumber={setFileNumber} showModal={showImportModal} setShowModal={setShowImportModal} />
          </>
          : <LeafItem leaf={workspace} root refreshContainer={() => { }} />
        }
      </ul>
    </ConverterCtx.Provider>
    <InboxElement count={fileNumber ?? 0} showModal={handleShowImportModal} className={workspace.type === "SolidLeaf" ? "inbox no-active" : "inbox"}
    />
  </div>;
}

const ContainerItem: FunctionComponent<{
  label: string,
  children: (SolidContainer | SolidLeaf)[],
} | {
  container: SolidContainer,
  root?: boolean,
}> = (args) => {
  const { session } = useSolidAuth();
  const setRefreshCount = useState(0)[1];
  const refresh = () => setRefreshCount(x => x + 1);
  const { t } = useTranslation("spoty");

  const [unfetched, uri, label, children] =
    "container" in args
      ? [args.container.isUnfetched(), args.container.uri, lastPart(args.container.uri), args.container.children()]
      : [false, undefined, args.label, args.children];

  const root = ("container" in args && args.root);

  if (label.startsWith(".")) {
    return null;
  }
  const tools = "container" in args ? <span className="tools">
    {session.isLoggedIn
      ? <LinkButton help={t("new folder") + " NOT IMPLEMENTED YET"} onClick={() => { }} disabled={true}>🖿 </LinkButton>
      : null}
    {session.isLoggedIn
      ? <UploadCollection container={args.container} onSuccess={refresh} />
      : null}
    {!root
      ? <WorkspaceLink to={args.container.uri}><abbr title={t("open as workspace")}>🞋</abbr></WorkspaceLink>
      : null}
    <ExtViewIcon href={uri as string} />
    <LinkIcon href={uri as string} />
  </span> : null;

  return <li><details open={true}>
    <summary>
      📂 {label} {tools}
    </summary>
    {unfetched
      ? <ul><li>⏳</li></ul>
      : <>
        <ul>
          {children.sort(cmpResource).map(c =>
            c.type === "SolidContainer"
              ? <ContainerItem key={c.uri} container={c} />
              : <LeafItem key={c.uri} leaf={c} refreshContainer={refresh} />
          )}
        </ul>
      </>
    }
  </details></li>
};

const LeafItem: FunctionComponent<{
  leaf: SolidLeaf,
  refreshContainer: () => void,
  root?: boolean,
}> = ({
  leaf,
  refreshContainer,
  root,
}) => {
    const label = decodeURIComponent(lastPart(leaf.uri));
  const { dataset } = useLdo();
  const { t } = useTranslation(["spoty", "translation"]);
  const { session } = useSolidAuth();

  const [errors, setErrors] = useState<string[]>();
  const [sentences, setSentences] = useState<string[]>();
  const [remoteSentenceCollection, setRemoteSentenceCollection] = useState<string[]>();
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedLeaf, setSharedLeaf] = useState<typeof leaf | null>(null);

  const isRemoteSentenceCollection = (leaf: string) => {
    return dataset
      .match(makeNamedNode(leaf), makeNamedNode(rdf.type), makeNamedNode(spoty.RemoteSentenceCollection))
      .size > 0;
  };

  // To get seeAlso
  useEffect(() => {
    setRemoteSentenceCollection(
      dataset
        .match(makeNamedNode(leaf.uri), makeNamedNode(rdfs.seeAlso), null)
        .toArray()
        .map(q => q.object.value)
    );
  }, [dataset, dataset.size, leaf]);

  useEffect(() => {
    if (!remoteSentenceCollection) return;

    setSentences(dataset
      .match(null, makeNamedNode(rdf.type), makeNamedNode(spoty.Sentence))
      .toArray()
      .map(q => q.subject.value)
      .filter(iri => iri.split('#')[0] === leaf.uri || remoteSentenceCollection.includes(iri.split('#')[0]))
      .sort(cmpStr)
    );
  }, [dataset, dataset.size, leaf, remoteSentenceCollection]);

  useEffect(() => {
    setErrors(
      dataset
        .match(makeNamedNode(leaf.uri), makeNamedNode(spoty.error), null)
        .toArray()
        .map(q => q.object.value)
        .sort(cmpStr)
    );
  }, [dataset, dataset.size, leaf]);

  const deleteLeaf = useCallback(() => {
    if (window.confirm(t("Confirm deleting \"{{label}}\"?", { label }))) {
      leaf.delete().then(refreshContainer);
    }
  }, [label, leaf, refreshContainer, t]);

  const ERR = <span className="error">⚠ </span>;

  return <li><details>
    <summary>
      { isRemoteSentenceCollection(leaf.uri)
      ? "✉️"
      : "📄"
      }
       {label}
      {" "} ({sentences?.length || "⏳"})
      {errors?.length ? <> {ERR}</> : null}
      <span className="tools">
        {session.isLoggedIn && !root
          ? <LinkButton help={t("delete")} onClick={deleteLeaf}>🗑️</LinkButton>
          : null}
        {!root
          ? <WorkspaceLink to={leaf.uri}><abbr title={t("open as workspace")}>🞋</abbr></WorkspaceLink>
          : null}
        <ExtViewIcon href={leaf.uri} />
        <LinkIcon href={leaf.uri} />
        {session.isLoggedIn && !isRemoteSentenceCollection(leaf.uri)
          ? <LinkButton help={t("share", { ns: "translation" })} onClick={() => { setSharedLeaf(leaf); setShowShareModal(true); }}>📤</LinkButton>
          : null}
        {sharedLeaf ?
          <ShareCollectionModal sharedLeaf={sharedLeaf} show={showShareModal} onClose={() => setShowShareModal(false)} />
          : null
        }
      </span>
    </summary>
    {leaf.isUnfetched() || errors === undefined || sentences === undefined
      ? <ul><li>⏳</li></ul>
      : <ul>
        {errors.map(e => <li key={e}>{ERR} {e}</li>)}
        {sentences.length > 0 ?
          (sentences.map(s => <li key={s}>💬 <SentenceLink uri={s} /></li>)) : (isRemoteSentenceCollection(leaf.uri)
            ? (<p>⚠️ {t("You don't have access to the sentences in this file.", { ns: "translation" })}</p>)
            : null)}
      </ul>
    }
  </details></li>;
};

const UploadCollection: FunctionComponent<{
  container: SolidContainer,
  onSuccess?: () => void,
}> = ({
  container,
  onSuccess,
}) => {
    const converter = useContext(ConverterCtx);
    const { t } = useTranslation("spoty");

    const fileInputRef = createRef<HTMLInputElement>();

    const buttonUploadFile = () => {
      fileInputRef.current?.click();
    }
    const uploadFiles = useCallback(async (evt: ChangeEvent<HTMLInputElement>) => {
      if (converter === undefined) { return; }

      for (let file of evt.target.files || []) {
        const baseName = file.name.substring(0, file.name.length - 5);
        const buffer = await (new Response(file).arrayBuffer());
        const array = new Uint8Array(buffer);
        const ttl = await converter.convert(array, file.name);
        // TODO handle warnings and errors, if any
        const res = await container.uploadChildIfAbsent(
          baseName as SolidLeafUri,
          new Blob([ttl]),
          "text/turtle",
        );
        if (res.isError) {
          alert(res.message);
        } else {
          console.debug("CREATED", res.uri);
          res.resource.read().then(() => onSuccess ? onSuccess() : null);
          container.read().then(() => onSuccess ? onSuccess() : null)
        }
      }
    }, [container, converter, onSuccess]);


    return (
      <LinkButton help={t("upload file")} onClick={buttonUploadFile} disabled={converter === null}>
        🗎
        <input type="file" ref={fileInputRef} accept=".xlsx" multiple={true} onChange={uploadFiles} style={{ display: "none" }} />
      </LinkButton>
    );
  }

export const ConverterCtx = createContext<Converter | undefined>(undefined);

export interface Converter {
  convert: (a: Uint8Array, n: string) => Promise<string>
};

const InboxElement: FunctionComponent<{
  count: number,
  showModal: () => void,
  className: string
}> = ({
  count,
  showModal,
  className
}) => {
    const { t } = useTranslation("translation");

    return <div onClick={showModal} className={className}>
      <span><abbr title={t("open inbox")}>📥</abbr></span>
      {count > 0 && (
        <span className="badge">{count}</span>
      )}
    </div>
  }

// This component is meant for debugging
export const ExtViewIcon: FunctionComponent<{ href: string }> = ({ href }) => {
  const { t } = useTranslation("spoty");

  const extHref = "https://penny.vincenttunru.com/explore/?url=" + encodeURIComponent(href);
  const app = "Penny";
  return <a href={extHref} className="extViewIcon"><abbr title={t("open in {{app}}", { app })}>🔎</abbr></a>;
}

// This component is meant for debugging
export const LinkIcon: FunctionComponent<{ href: string }> = ({ href }) => {
  const { t } = useTranslation("spoty");

  return <a href={href}><abbr title={t("access raw data")}>🔗</abbr></a>;
}
